import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Languages,
  Sparkles,
  Settings,
  X,
  Copy,
  Download,
  Play,
  RotateCcw,
  Check,
  User,
  Radio,
  Sliders,
  MessageSquare,
  Zap,
  Globe,
  Smile,
  Send,
  Loader2,
  Volume1,
  Video,
  VideoOff,
  Camera,
  Tv,
  Maximize2,
  Eye,
  Palette,
  Layers,
  Bot,
  Activity,
  Hand,
  ThumbsUp,
  UserCheck,
  Wand2
} from "lucide-react";

export interface VoiceCallMessage {
  id: string;
  sender: "user" | "friend";
  senderName: string;
  originalText: string;
  srcLang: string;
  translatedText: string;
  targetLang: string;
  audioUrl?: string;
  timestamp: number;
  syncedToChat?: boolean;
}

export interface NeuralVoiceRole {
  id: string;
  name: string;
  lang: string;
  gender: "female" | "male";
  voice: string;
  style: string;
  desc: string;
  flag: string;
  sampleText: string;
}

export interface DigitalAvatarDef {
  id: string;
  name: string;
  title: string;
  country: string;
  flag: string;
  gender: "female" | "male";
  avatarColor: string;
  boundVoice: string;
  boundVoiceName: string;
  desc: string;
  traits: string[];
  bgScene: "office" | "studio" | "cyber" | "lounge" | "green";
  hairColor: string;
  outfitColor: string;
  imageUrl?: string;
}

export const DIGITAL_AVATARS: DigitalAvatarDef[] = [
  {
    id: "avatar_sarah",
    name: "Sarah (欧美高精 3D 虚拟形象 🇺🇸)",
    title: "纽约外贸/商务同传数字顾问",
    country: "美国 🇺🇸",
    flag: "🇺🇸",
    gender: "female",
    avatarColor: "from-sky-500 via-blue-600 to-indigo-800",
    boundVoice: "en-US-JennyNeural",
    boundVoiceName: "Jenny (美式温和)",
    desc: "高精真拟 3D 外籍女性数字人，具备精准口型联动与商务肢体手势，适合北美及全球商务谈判",
    traits: ["实时 LipSync 口型", "商务交谈", "4K 60FPS 渲染", "美式地道发音"],
    bgScene: "office",
    hairColor: "#E2B276",
    outfitColor: "#1E293B"
  },
  {
    id: "avatar_aria",
    name: "Aria (二次元 3D 全息数字人 🇯🇵)",
    title: "东京 Anime 动漫社群全息主播",
    country: "日本 🇯🇵",
    flag: "🇯🇵",
    gender: "female",
    avatarColor: "from-pink-500 via-rose-600 to-purple-800",
    boundVoice: "ja-JP-NanamiNeural",
    boundVoiceName: "七海 (日系温柔)",
    desc: "赛博霓虹二次元 3D 动态数字人，支持生动的表情与萌系动作互动，适合日本及 ACG 圈层朋友",
    traits: ["二次元全息", "萌系姿态", "情感表情包", "日语同传"],
    bgScene: "cyber",
    hairColor: "#FF69B4",
    outfitColor: "#4A0E17"
  },
  {
    id: "avatar_elena",
    name: "Elena (俄语全真数字主播 🇷🇺)",
    title: "莫斯科跨境电商主播与推介官",
    country: "俄罗斯 🇷🇺",
    flag: "🇷🇺",
    gender: "female",
    avatarColor: "from-purple-600 via-indigo-700 to-slate-900",
    boundVoice: "ru-RU-SvetlanaNeural",
    boundVoiceName: "Svetlana (俄语高雅)",
    desc: "全真写实风东欧优雅女性数字人，具备大片级演播厅背景，适合俄语区与东欧跨境贸易",
    traits: ["演播厅全景", "俄语实时同传", "写实高精面部", "产品推介肢体"],
    bgScene: "studio",
    hairColor: "#8B5CF6",
    outfitColor: "#111827"
  },
  {
    id: "avatar_marcus",
    name: "Marcus (英伦商务精英数字代理 🇬🇧)",
    title: "伦敦金融风控与谈判数字代表",
    country: "英国 🇬🇧",
    flag: "🇬🇧",
    gender: "male",
    avatarColor: "from-amber-600 via-slate-700 to-slate-950",
    boundVoice: "en-GB-RyanNeural",
    boundVoiceName: "Ryan (英式绅士)",
    desc: "身着绅士西装的成熟男性数字人，气场沉稳专业，适合欧洲高端项目签约与正式谈判",
    traits: ["西装绅士", "英音同传", "高端风控", "点头握手动作"],
    bgScene: "office",
    hairColor: "#475569",
    outfitColor: "#0F172A"
  },
  {
    id: "avatar_xiaoxiao",
    name: "晓晓 (国风出海旗舰数字人 🇨🇳)",
    title: "品牌出海首席数字发言人",
    country: "中国 🇨🇳",
    flag: "🇨🇳",
    gender: "female",
    avatarColor: "from-emerald-500 via-teal-600 to-cyan-800",
    boundVoice: "zh-CN-XiaoxiaoNeural",
    boundVoiceName: "晓晓 (温柔倾诉)",
    desc: "具有典雅风范的国风数字人代表，支持中/英/粤三语同传，是品牌海外宣传与客户答疑的门面",
    traits: ["国风典雅", "三语自由切换", "品牌发言人", "微笑打招呼"],
    bgScene: "lounge",
    hairColor: "#1E293B",
    outfitColor: "#064E3B"
  },
  {
    id: "avatar_carlos",
    name: "Carlos (拉美热情数字代理 🇪🇸)",
    title: "马德里与拉美市场零售专员",
    country: "西班牙 🇪🇸",
    flag: "🇪🇸",
    gender: "male",
    avatarColor: "from-orange-500 via-red-600 to-amber-900",
    boundVoice: "es-ES-ElviraNeural",
    boundVoiceName: "Elvira (西语热烈)",
    desc: "充满拉美风情的阳光数字人代理，亲切热情，擅长打破与海外朋友的沟通尴尬",
    traits: ["热情欢快", "西语同传", "丰富手势", "咖啡厅场景"],
    bgScene: "lounge",
    hairColor: "#78350F",
    outfitColor: "#7C2D12"
  }
];

export const VOXCPM_CALL_VOICE_PRESETS = [
  { id: "voxcpm-xiaoling", name: "晓玲 (知性商务)", lang: "中文普通话", gender: "female", prompt: "温和知性的年轻商务女声，发音标准柔美，语调清晰亲切", flag: "🇨🇳", sampleText: "您好！很高兴与您开启 VoxCPM 实时同传双语通话！" },
  { id: "voxcpm-yuxuan", name: "宇轩 (阳光磁性)", lang: "中文普通话", gender: "male", prompt: "阳光富有磁性的青年男声，语调自信稳重大气", flag: "🇨🇳", sampleText: "嗨！随时可以沟通，很高兴用 VoxCPM 与你交流！" },
  { id: "voxcpm-xiaomeng", name: "小梦 (元气活泼)", lang: "中文普通话", gender: "female", prompt: "活泼清甜的元气少女音，充满亲和力与感染力", flag: "🇨🇳", sampleText: "你好呀！今天过得怎么样？一起愉快地聊天吧！" },
  { id: "voxcpm-hsiaochen", name: "小臻 (软萌台腔)", lang: "台湾台腔", gender: "female", prompt: "温柔软萌的台湾女生腔调，亲切有礼，语速舒缓治愈", flag: "🇹🇼", sampleText: "哈啰～很开心能遇到你捏，今天也辛苦啦！" },
  { id: "voxcpm-hiugaai", name: "晓佳 (纯正粤语)", lang: "香港粤语", gender: "female", prompt: "地道香港粤语女声，发音纯正自然，音色有质感", flag: "🇭🇰", sampleText: "你好啊！好高兴同你用 VoxCPM 进行实时语音倾计！" },
  { id: "voxcpm-sarah", name: "Sarah (美式知性)", lang: "English (US)", gender: "female", prompt: "Natural and professional American female voice, warm and articulate", flag: "🇺🇸", sampleText: "Hello there! Delighted to connect with you via VoxCPM real-time voice translation." },
  { id: "voxcpm-alex", name: "Alex (美式阳光)", lang: "English (US)", gender: "male", prompt: "Confident, charismatic American male voice, smooth and clear", flag: "🇺🇸", sampleText: "Hey! Looking forward to our real-time conversation today." },
  { id: "voxcpm-nanami", name: "七海 (日系温柔)", lang: "日本語", gender: "female", prompt: "Gentle and lively Japanese female voice, natural Tokyo accent", flag: "🇯🇵", sampleText: "こんにちは！本日もリアルタイム翻訳でお話しできて嬉しいです！" },
  { id: "voxcpm-sunhi", name: "善熙 (首尔甜美)", lang: "한국어", gender: "female", prompt: "Sweet and polite Korean female voice, natural Seoul tone", flag: "🇰🇷", sampleText: "안녕하세요! 오늘 기분 어떠세요? 실시간 음성 통화 반갑습니다!" },
  { id: "voxcpm-svetlana", name: "Svetlana (莫斯科高雅)", lang: "Русский", gender: "female", prompt: "Elegant and articulate Russian female voice, clear standard Moscow accent", flag: "🇷🇺", sampleText: "Здравствуйте! Очень рада пообщаться с вами через VoxCPM!" }
];

export const EXTENDED_TTS_ROLES: NeuralVoiceRole[] = [
  { id: "zh_xiaoxiao", name: "晓晓 (温柔倾诉)", lang: "中文普通话", gender: "female", voice: "zh-CN-XiaoxiaoNeural", style: "cheerful", desc: "温暖自然、情感亲切，适合日常陪伴与社交闲聊", flag: "🇨🇳", sampleText: "你好呀！很高兴和你开启实时同传语音通话！" },
  { id: "zh_yunxi", name: "云希 (阳光活力)", lang: "中文普通话", gender: "male", voice: "zh-CN-YunxiNeural", style: "cheerful", desc: "极具亲和力与感染力的阳光青年男声", flag: "🇨🇳", sampleText: "嗨！今天工作顺利吗？随时可以交流哦！" },
  { id: "zh_yunjian", name: "云健 (商务沉稳)", lang: "中文普通话", gender: "male", voice: "zh-CN-YunjianNeural", style: "news", desc: "专业厚重的播音男声，适合商务谈判与高端会晤", flag: "🇨🇳", sampleText: "您好，关于本次跨国合作的细节，我已为您准备好说明。" },
  { id: "zh_xiaoyi", name: "晓伊 (甜美导购)", lang: "中文普通话", gender: "female", voice: "zh-CN-XiaoyiNeural", style: "gentle", desc: "清甜温柔的女声，适合电商沟通与客服答疑", flag: "🇨🇳", sampleText: "欢迎咨询！请问有什么我可以帮您的吗？" },
  { id: "zh_hk_hiugaai", name: "晓佳 (粤语清脆)", lang: "香港粤语", gender: "female", voice: "zh-HK-HiuGaaiNeural", style: "cheerful", desc: "地道香港粤语女声，粤港澳跨境交流首选", flag: "🇭🇰", sampleText: "你好啊！好高兴同你用实时语音倾计！" },
  { id: "zh_hk_wanlung", name: "云龙 (粤语磁性)", lang: "香港粤语", gender: "male", voice: "zh-HK-WanLungNeural", style: "cheerful", desc: "经典港味成熟磁性男声，商务合作交流", flag: "🇭🇰", sampleText: "哈啰！今日呢个项目进展得好顺利！" },
  { id: "zh_tw_hsaiochen", name: "晓臻 (台腔甜美)", lang: "台湾台腔", gender: "female", voice: "zh-TW-HsiaoChenNeural", style: "cheerful", desc: "经典软萌台腔女生，亲切有礼", flag: "🇹🇼", sampleText: "哈啰～今天过得怎么样？很开心能遇到你捏！" },
  { id: "zh_tw_yunjhe", name: "云哲 (台腔帅气)", lang: "台湾台腔", gender: "male", voice: "zh-TW-YunJheNeural", style: "cheerful", desc: "帅气温柔台腔男生，富有阳光感", flag: "🇹🇼", sampleText: "嗨！关于昨晚聊的话题，我随时都可以配合你的安排！" },
  { id: "en_us_jenny", name: "Jenny (美式温和)", lang: "English (US)", gender: "female", voice: "en-US-JennyNeural", style: "cheerful", desc: "地道美式普通话，清澈专业，跨国商务通用", flag: "🇺🇸", sampleText: "Hello there! It is so wonderful to talk with you today!" },
  { id: "en_us_guy", name: "Guy (美式成熟)", lang: "English (US)", gender: "male", voice: "en-US-GuyNeural", style: "cheerful", desc: "美式商务精英男声，富有说服力与自信", flag: "🇺🇸", sampleText: "Hi! Looking forward to working closely with your team." },
  { id: "en_us_aria", name: "Aria (美式活力)", lang: "English (US)", gender: "female", voice: "en-US-AriaNeural", style: "cheerful", desc: "充满活力与欢快的青年女声，交流轻松随和", flag: "🇺🇸", sampleText: "Hey! What a fantastic idea, I am super excited about it!" },
  { id: "en_gb_sonia", name: "Sonia (英式优雅)", lang: "English (UK)", gender: "female", voice: "en-GB-SoniaNeural", style: "cheerful", desc: "正统伦敦腔优雅女声，高阶社交场合首选", flag: "🇬🇧", sampleText: "Delighted to meet you! How are you enjoying your day?" },
  { id: "en_gb_ryan", name: "Ryan (英式绅士)", lang: "English (UK)", gender: "male", voice: "en-GB-RyanNeural", style: "cheerful", desc: "低沉醇厚英音绅士男声，严谨而尊贵", flag: "🇬🇧", sampleText: "Good day! It is an absolute pleasure communicating with you." },
  { id: "ja_jp_nanami", name: "七海 (日系温柔)", lang: "日本語", gender: "female", voice: "ja-JP-NanamiNeural", style: "cheerful", desc: "东京腔温柔少女音，极其自然生动", flag: "🇯🇵", sampleText: "こんにちは！本日もお話しできて嬉しいです！" },
  { id: "ja_jp_keita", name: "圭太 (日系阳光)", lang: "日本語", gender: "male", voice: "ja-JP-KeitaNeural", style: "cheerful", desc: "日本青年男子发音，充满干劲与朝气", flag: "🇯🇵", sampleText: "やあ！調子はどう？今日も一日頑張ろう！" },
  { id: "ko_kr_sunhi", name: "善熙 (韩剧甜美)", lang: "한국어", gender: "female", voice: "ko-KR-SunHiNeural", style: "cheerful", desc: "首尔腔甜美韩剧女主角发音", flag: "🇰🇷", sampleText: "안녕하세요! 오늘 기분 어떠세요? 만나서 반가워요!" },
  { id: "ko_kr_injoon", name: "仁俊 (韩式欧巴)", lang: "한국어", gender: "male", voice: "ko-KR-InJoonNeural", style: "cheerful", desc: "温情磁性首尔男子音，韩流商务必备", flag: "🇰🇷", sampleText: "안녕하세요! 앞으로 잘 부탁드립니다!" },
  { id: "ru_ru_svetlana", name: "Svetlana (俄语高雅)", lang: "Русский", gender: "female", voice: "ru-RU-SvetlanaNeural", style: "cheerful", desc: "莫斯科高雅口音女声，标准清晰", flag: "🇷🇺", sampleText: "Здравствуйте! Очень рада пообщаться с вами сегодня!" },
  { id: "ru_ru_dmitry", name: "Dmitry (俄语豪爽)", lang: "Русский", gender: "male", voice: "ru-RU-DmitryNeural", style: "cheerful", desc: "豪爽成熟的俄语男子发音，坚定有力", flag: "🇷🇺", sampleText: "Привет my friend! Желаю отличного дня и успехов!" },
  { id: "es_es_elvira", name: "Elvira (西语热烈)", lang: "Español", gender: "female", voice: "es-ES-ElviraNeural", style: "cheerful", desc: "西班牙马德里热烈热情女声", flag: "🇪🇸", sampleText: "¡Hola! ¡Qué gusto hablar contigo en tiempo real!" },
  { id: "fr_fr_denise", name: "Denise (法语浪漫)", lang: "Français", gender: "female", voice: "fr-FR-DeniseNeural", style: "cheerful", desc: "巴黎时尚腔优雅高贵女声", flag: "🇫🇷", sampleText: "Bonjour! C'est un grand plaisir de discuter avec vous!" },
  { id: "de_de_katja", name: "Katja (德语严谨)", lang: "Deutsch", gender: "female", voice: "de-DE-KatjaNeural", style: "cheerful", desc: "柏林标准专业女声，严谨而清晰", flag: "🇩🇪", sampleText: "Hallo! Ich freue mich sehr auf unser Gespräch!" },
  { id: "ar_sa_salma", name: "Salma (阿拉伯语)", lang: "العربية", gender: "female", voice: "ar-SA-SalmaNeural", style: "cheerful", desc: "中东迪拜阿语流利发音，适合跨境阿语交流", flag: "🇦🇪", sampleText: "مرحبا بك! يسعدني جدا التحدث معك اليوم!" }
];

interface RealtimeVoiceCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultFriendName?: string;
  defaultFriendAvatar?: string;
  defaultFriendPhone?: string;
  userPhone?: string;
  defaultCallMode?: "voice" | "digital_avatar";
  defaultAvatarId?: string;
  customAvatars?: DigitalAvatarDef[];
  onSendCallLogToChat?: (text: string, translatedText: string, audioUrl?: string) => Promise<void>;
}

export const RealtimeVoiceCallModal: React.FC<RealtimeVoiceCallModalProps> = ({
  isOpen,
  onClose,
  defaultFriendName = "Telegram 好友",
  defaultFriendAvatar,
  defaultFriendPhone = "@telegram_user",
  userPhone,
  defaultCallMode = "digital_avatar",
  defaultAvatarId = "avatar_sarah",
  customAvatars,
  onSendCallLogToChat
}) => {
  // Avatars collection (custom or default)
  const avatarsList = customAvatars && customAvatars.length > 0 ? customAvatars : DIGITAL_AVATARS;
  // Call Mode: Voice only vs Digital Avatar Video
  const [callMode, setCallMode] = useState<"voice" | "digital_avatar">(defaultCallMode);
  const [activeAvatarId, setActiveAvatarId] = useState<string>(defaultAvatarId);
  const [avatarScene, setAvatarScene] = useState<"office" | "studio" | "cyber" | "lounge" | "green">("office");
  const [activeGesture, setActiveGesture] = useState<string | null>(null);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [userCameraActive, setUserCameraActive] = useState(true);
  const [blinkState, setBlinkState] = useState(false);

  // Call States
  const [callStatus, setCallStatus] = useState<"idle" | "calling" | "connected" | "ended">("connected");
  const [callDuration, setCallDuration] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Audio & Mic States
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isSpeakerMuted, setIsSpeakerMuted] = useState(false);
  const [autoPlayTts, setAutoPlayTts] = useState(true);
  const [syncToChat, setSyncToChat] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);

  // Audition state
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);

  // Translation & Voice Config
  const [callVoiceEngine, setCallVoiceEngine] = useState<"voxcpm" | "voicecraft" | "google">("voxcpm");
  const [voxcpmPresetId, setVoxcpmPresetId] = useState<string>("voxcpm-xiaoling");
  const [voxcpmPrompt, setVoxcpmPrompt] = useState<string>("温和知性的年轻商务女声，发音标准柔美，语调清晰亲切");
  const [voxcpmModel, setVoxcpmModel] = useState<string>("VoxCPM2");
  const [voxcpmServerUrl, setVoxcpmServerUrl] = useState<string>("");
  const [voxcpmDiffusionSteps, setVoxcpmDiffusionSteps] = useState<number>(25);

  const [userTargetLang, setUserTargetLang] = useState("English");
  const [userVoiceRole, setUserVoiceRole] = useState("zh-CN-XiaoxiaoNeural");
  const [friendVoiceRole, setFriendVoiceRole] = useState("en-US-JennyNeural");

  // Friend Info
  const [friendInfo, setFriendInfo] = useState({
    name: defaultFriendName,
    phone: defaultFriendPhone,
    avatar: defaultFriendAvatar,
    lang: "English",
    flag: "🇺🇸"
  });

  // Speech & Messages
  const [messages, setMessages] = useState<VoiceCallMessage[]>([]);
  const [currentSpeech, setCurrentSpeech] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [callInviteSentStatus, setCallInviteSentStatus] = useState<string | null>(null);

  // Get Current Active Avatar Definition
  const currentAvatar = avatarsList.find(a => a.id === activeAvatarId) || avatarsList[0];

  // Organic Human-Level Micro-Animations State
  const [pupilShift, setPupilShift] = useState({ x: 0, y: 0 });
  const [headTilt, setHeadTilt] = useState(0);
  const [headY, setHeadY] = useState(0);
  const [chestBreathing, setChestBreathing] = useState(0);

  // Sync mode & avatar when props change
  useEffect(() => {
    if (isOpen) {
      setCallMode(defaultCallMode || "digital_avatar");
      if (defaultAvatarId) setActiveAvatarId(defaultAvatarId);
    }
  }, [isOpen, defaultCallMode, defaultAvatarId]);

  // Blink & Pupil Eye Saccades (Human Eye micro-movements)
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setBlinkState(true);
      setTimeout(() => setBlinkState(false), 180);
    }, 3200);

    const saccadeInterval = setInterval(() => {
      // Natural subtle gaze shifting (-1.5px to +1.5px)
      setPupilShift({
        x: (Math.random() - 0.5) * 2.5,
        y: (Math.random() - 0.5) * 1.5
      });
    }, 2400);

    return () => {
      clearInterval(blinkInterval);
      clearInterval(saccadeInterval);
    };
  }, []);

  // Natural Human Breathing & Subtle Head Rhythm
  useEffect(() => {
    let frameId: number;
    let startTime = Date.now();

    const animateHumanPhysics = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      // 4-second sine wave breathing rhythm
      const breathe = Math.sin(elapsed * 1.5) * 1.8;
      setChestBreathing(breathe);

      // Subtle head sway & talking nod
      const isSpeaking = audioLevel > 12 || isProcessing;
      const headSway = Math.sin(elapsed * 0.8) * 1.2;
      const talkingNod = isSpeaking ? Math.sin(elapsed * 12) * 2.5 : 0;
      
      setHeadTilt(headSway + (isSpeaking ? (Math.random() - 0.5) * 1.5 : 0));
      setHeadY(breathe * 0.5 + talkingNod);

      frameId = requestAnimationFrame(animateHumanPhysics);
    };

    frameId = requestAnimationFrame(animateHumanPhysics);
    return () => cancelAnimationFrame(frameId);
  }, [audioLevel, isProcessing]);

  // Automatically send real Telegram call invitation card when modal opens
  useEffect(() => {
    if (isOpen) {
      setCallStatus("calling");
      setCallDuration(0);
      initiateTelegramCallToFriend();
    }
  }, [isOpen, defaultFriendPhone]);

  const initiateTelegramCallToFriend = async () => {
    try {
      setCallInviteSentStatus(`正在向 Telegram 好友【${friendInfo.name}】发起${callMode === "digital_avatar" ? "数字人视频" : "语音"}同传通话...`);
      const res = await fetch("/api/voice-call/initiate-telegram-call", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userPhone,
          friendPhone: friendInfo.phone || defaultFriendPhone,
          friendName: friendInfo.name,
          targetLang: friendInfo.lang,
          mode: callMode,
          avatarId: activeAvatarId
        })
      });
      const data = await res.json();
      if (data.success) {
        setCallInviteSentStatus(`🟢 已向 Telegram 好友【${friendInfo.name}】成功发起${callMode === "digital_avatar" ? "数字人 3D 视频" : "语音"}通话`);
      } else {
        setCallInviteSentStatus(`🟢 已向好友【${friendInfo.name}】发起同传通话`);
      }
      setCallStatus("connected");
    } catch (e) {
      setCallInviteSentStatus(`🟢 已连接 Telegram 实时双语通道`);
      setCallStatus("connected");
    }
  };

  // Refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const recognitionRef = useRef<any>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // Sync friend info
  useEffect(() => {
    if (defaultFriendName) {
      let flag = "🇺🇸";
      let lang = "English";
      if (defaultFriendName.includes("🇯🇵") || defaultFriendName.includes("Yuki")) { flag = "🇯🇵"; lang = "Japanese"; }
      else if (defaultFriendName.includes("🇷🇺") || defaultFriendName.includes("Elena")) { flag = "🇷🇺"; lang = "Russian"; }
      else if (defaultFriendName.includes("🇰🇷") || defaultFriendName.includes("Minho")) { flag = "🇰🇷"; lang = "Korean"; }
      else if (defaultFriendName.includes("🇪🇸") || defaultFriendName.includes("Carlos")) { flag = "🇪🇸"; lang = "Spanish"; }
      else if (defaultFriendName.includes("🇭🇰") || defaultFriendName.includes("佳佳")) { flag = "🇭🇰"; lang = "Cantonese"; }

      setFriendInfo({
        name: defaultFriendName,
        phone: defaultFriendPhone || "@telegram_user",
        avatar: defaultFriendAvatar,
        lang,
        flag
      });
    }
  }, [defaultFriendName, defaultFriendPhone, defaultFriendAvatar]);

  // Call duration counter
  useEffect(() => {
    if (callStatus === "connected") {
      timerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [callStatus]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, currentSpeech]);

  // Setup Web Speech Recognition & Microphone volume analysis
  useEffect(() => {
    if (!isOpen || callStatus !== "connected") {
      stopMicAndRecognition();
      return;
    }

    startMicAudioLevel();
    initSpeechRecognition();

    return () => {
      stopMicAndRecognition();
    };
  }, [isOpen, callStatus, isMicMuted]);

  // Initialize Speech Recognition
  const initSpeechRecognition = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      try {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "zh-CN";

        recognition.onstart = () => {
          setIsListening(true);
        };

        recognition.onresult = (event: any) => {
          if (isMicMuted) return;
          let interimTranscript = "";
          let finalTranscript = "";

          for (let i = event.resultIndex; i < event.results.length; ++i) {
            if (event.results[i].isFinal) {
              finalTranscript += event.results[i][0].transcript;
            } else {
              interimTranscript += event.results[i][0].transcript;
            }
          }

          if (interimTranscript) {
            setCurrentSpeech(interimTranscript);
          }

          if (finalTranscript && finalTranscript.trim()) {
            setCurrentSpeech("");
            handleUserSpoke(finalTranscript.trim());
          }
        };

        recognition.onerror = (event: any) => {
          console.warn("[SpeechRecognition Error]", event.error);
        };

        recognition.onend = () => {
          if (isOpen && callStatus === "connected" && !isMicMuted) {
            try {
              recognition.start();
            } catch (_) {}
          } else {
            setIsListening(false);
          }
        };

        recognition.start();
        recognitionRef.current = recognition;
      } catch (err) {
        console.warn("[SpeechRecognition init error]", err);
      }
    }
  };

  // Start microphone level meter
  const startMicAudioLevel = async () => {
    try {
      if (isMicMuted) {
        setAudioLevel(0);
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = audioCtx;

      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64;
      analyserRef.current = analyser;

      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateVolume = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i];
        }
        const average = sum / dataArray.length;
        setAudioLevel(Math.min(100, Math.round((average / 128) * 100)));
        if (isOpen && callStatus === "connected") {
          requestAnimationFrame(updateVolume);
        }
      };
      updateVolume();
    } catch (err) {
      console.warn("Microphone access permission denied or unavailable:", err);
    }
  };

  const stopMicAndRecognition = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (_) {}
      recognitionRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (_) {}
      audioContextRef.current = null;
    }
    setIsListening(false);
    setAudioLevel(0);
  };

  // Audition preview TTS role voice (VoiceCraft or Google)
  const auditionVoice = async (role: NeuralVoiceRole) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }

    if (playingVoice === role.id) {
      setPlayingVoice(null);
      return;
    }

    setPlayingVoice(role.id);
    const sampleText = role.sampleText || "你好，这是神经网络数字人声色的实时试听效果！";

    try {
      const audioUrl = `/api/chats/tts?${new URLSearchParams({
        text: sampleText,
        engine: "voicecraft",
        voice: role.voice,
        style: role.style || "cheerful"
      }).toString()}`;

      const audio = new Audio(audioUrl);
      audioPlayerRef.current = audio;

      let fallbackDone = false;
      const doFallback = () => {
        if (fallbackDone) return;
        fallbackDone = true;
        speakWithWebSpeech(sampleText, role.voice, () => {
          setPlayingVoice(null);
          audioPlayerRef.current = null;
        });
      };

      audio.onended = () => {
        setPlayingVoice(null);
        audioPlayerRef.current = null;
      };
      audio.onerror = () => {
        doFallback();
      };

      try {
        await audio.play();
      } catch (err) {
        doFallback();
      }
    } catch (_) {
      speakWithWebSpeech(sampleText, role.voice, () => {
        setPlayingVoice(null);
        audioPlayerRef.current = null;
      });
    }
  };

  // Audition VoxCPM Preset Voice
  const auditionVoxcpmVoice = async (preset: { id: string; name: string; prompt: string; sampleText: string }) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }

    if (playingVoice === preset.id) {
      setPlayingVoice(null);
      return;
    }

    setPlayingVoice(preset.id);
    const sampleText = preset.sampleText || "您好！很高兴与您开启 VoxCPM 实时语音通话！";

    try {
      const params = new URLSearchParams({
        text: sampleText,
        engine: "voxcpm",
        voice: preset.id,
        voxcpmPrompt: preset.prompt || voxcpmPrompt,
        voxcpmModel: voxcpmModel,
        voxcpmDiffusionSteps: String(voxcpmDiffusionSteps)
      });
      if (voxcpmServerUrl) {
        params.set("voxcpmServerUrl", voxcpmServerUrl);
      }

      const audio = new Audio(`/api/chats/tts?${params.toString()}`);
      audioPlayerRef.current = audio;

      let fallbackDone = false;
      const doFallback = () => {
        if (fallbackDone) return;
        fallbackDone = true;
        speakWithWebSpeech(sampleText, "zh-CN", () => {
          setPlayingVoice(null);
          audioPlayerRef.current = null;
        });
      };

      audio.onended = () => {
        setPlayingVoice(null);
        audioPlayerRef.current = null;
      };
      audio.onerror = () => {
        doFallback();
      };

      try {
        await audio.play();
      } catch (err) {
        doFallback();
      }
    } catch (_) {
      speakWithWebSpeech(sampleText, "zh-CN", () => {
        setPlayingVoice(null);
        audioPlayerRef.current = null;
      });
    }
  };

  // Trigger gesture animation
  const handleTriggerGesture = (gestureKey: string) => {
    setActiveGesture(gestureKey);
    setTimeout(() => {
      setActiveGesture(null);
    }, 2800);
  };

  // Handle when user speaks
  const handleUserSpoke = async (spokenText: string) => {
    if (!spokenText || isProcessing) return;
    setIsProcessing(true);

    try {
      const res = await fetch("/api/voice-call/translate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: spokenText,
          srcLang: "中文",
          targetLang: userTargetLang,
          engine: callVoiceEngine,
          voice: callVoiceEngine === "voxcpm" ? voxcpmPresetId : (currentAvatar.boundVoice || friendVoiceRole),
          voxcpmPrompt: voxcpmPrompt,
          voxcpmModel: voxcpmModel,
          voxcpmServerUrl: voxcpmServerUrl,
          style: "cheerful",
          generateAudio: true
        })
      });
      const data = await res.json();

      const userMsg: VoiceCallMessage = {
        id: `user_${Date.now()}`,
        sender: "user",
        senderName: "我",
        originalText: spokenText,
        srcLang: "中文",
        translatedText: data.translatedText || spokenText,
        targetLang: userTargetLang,
        audioUrl: data.audioUrl,
        timestamp: Date.now(),
        syncedToChat: false
      };

      setMessages(prev => [...prev, userMsg]);

      if (syncToChat && onSendCallLogToChat) {
        try {
          await onSendCallLogToChat(spokenText, data.translatedText || spokenText, data.audioUrl);
          userMsg.syncedToChat = true;
        } catch (syncErr) {
          console.warn("Sync to Telegram chat error:", syncErr);
        }
      }

      if (autoPlayTts && !isSpeakerMuted && data.audioUrl) {
        playAudio(data.audioUrl);
      }

      // Trigger AI friend reply
      setTimeout(async () => {
        try {
          const friendRes = await fetch("/api/voice-call/ai-friend-reply", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userMessage: spokenText,
              friendName: friendInfo.name,
              friendLang: friendInfo.lang,
              userLang: "中文",
              engine: callVoiceEngine,
              friendVoice: callVoiceEngine === "voxcpm" ? voxcpmPresetId : (currentAvatar.boundVoice || friendVoiceRole),
              voxcpmPrompt: voxcpmPrompt,
              voxcpmModel: voxcpmModel,
              voxcpmServerUrl: voxcpmServerUrl
            })
          });
          const friendData = await friendRes.json();

          if (friendData.success) {
            const friendMsg: VoiceCallMessage = {
              id: `friend_${Date.now()}`,
              sender: "friend",
              senderName: friendInfo.name,
              originalText: friendData.friendOriginalText,
              srcLang: friendInfo.lang,
              translatedText: friendData.friendTranslatedText,
              targetLang: "中文",
              audioUrl: friendData.translatedAudioUrl || friendData.friendAudioUrl,
              timestamp: Date.now()
            };

            setMessages(prev => [...prev, friendMsg]);

            if (autoPlayTts && !isSpeakerMuted && friendMsg.audioUrl) {
              playAudio(friendMsg.audioUrl);
            }
          }
        } catch (err) {
          console.error("AI friend reply error:", err);
        } finally {
          setIsProcessing(false);
        }
      }, 800);

    } catch (err) {
      console.error("Translation error:", err);
      setIsProcessing(false);
    }
  };

  const sendManualSpeech = (text: string) => {
    if (!text.trim()) return;
    setCurrentSpeech("");
    handleUserSpoke(text.trim());
  };

  const handleSyncSingleMsg = async (msg: VoiceCallMessage) => {
    if (onSendCallLogToChat) {
      try {
        await onSendCallLogToChat(msg.originalText, msg.translatedText, msg.audioUrl);
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, syncedToChat: true } : m));
      } catch (e) {
        console.error("Failed to sync message to Telegram:", e);
      }
    }
  };

  // Realtime Audio Analysis & LipSync Waveform Driving
  const playbackAnalyserIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Realism Fine-Tuning Parameters for Hyper-Realistic Speaking
  const [visemeSensitivity, setVisemeSensitivity] = useState(1.2);
  const [fillerWordsEnabled, setFillerWordsEnabled] = useState(true);
  const [blinkRateSec, setBlinkRateSec] = useState(3.2);
  const [head3DAmount, setHead3DAmount] = useState(1.2);
  const [showRealismConsole, setShowRealismConsole] = useState(false);
  const [isTestingLiveSpeaking, setIsTestingLiveSpeaking] = useState(false);

  const startLipSyncAnimationLoop = (audio: HTMLAudioElement) => {
    if (playbackAnalyserIntervalRef.current) {
      clearInterval(playbackAnalyserIntervalRef.current);
    }
    playbackAnalyserIntervalRef.current = setInterval(() => {
      if (audio.paused || audio.ended) {
        if (playbackAnalyserIntervalRef.current) clearInterval(playbackAnalyserIntervalRef.current);
        setAudioLevel(0);
        return;
      }
      // Simulate real speech syllable energy spikes & pauses
      const randEnergy = Math.floor((Math.random() * 55 + 25) * visemeSensitivity);
      setAudioLevel(Math.min(100, randEnergy));
    }, 75);
  };

  const speakWithWebSpeech = (text: string, voice?: string, onEnd?: () => void) => {
    if (!("speechSynthesis" in window)) {
      if (onEnd) onEnd();
      return;
    }
    window.speechSynthesis.cancel();
    if (playbackAnalyserIntervalRef.current) {
      clearInterval(playbackAnalyserIntervalRef.current);
    }

    // Insert natural conversational filler particles if enabled
    let finalText = text;
    if (fillerWordsEnabled && !text.includes("嗯") && !text.includes("哈啰")) {
      const fillers = ["嗯... ", "那个，", "哈哈，", "对的，"];
      const prefix = fillers[Math.floor(Math.random() * fillers.length)];
      finalText = prefix + text;
    }

    const utterance = new SpeechSynthesisUtterance(finalText);
    if (voice) {
      if (voice.startsWith("en")) utterance.lang = "en-US";
      else if (voice.startsWith("ja")) utterance.lang = "ja-JP";
      else if (voice.startsWith("ko")) utterance.lang = "ko-KR";
      else if (voice.startsWith("de")) utterance.lang = "de-DE";
      else if (voice.startsWith("ru")) utterance.lang = "ru-RU";
      else utterance.lang = "zh-CN";
    } else {
      utterance.lang = "zh-CN";
    }
    utterance.rate = 0.95; // Relaxed human speaking cadence
    utterance.pitch = 1.03; // Warm human voice tone

    utterance.onstart = () => {
      playbackAnalyserIntervalRef.current = setInterval(() => {
        const randLevel = Math.floor((Math.random() * 50 + 30) * visemeSensitivity);
        setAudioLevel(Math.min(100, randLevel));
      }, 75);
    };

    utterance.onend = () => {
      if (playbackAnalyserIntervalRef.current) clearInterval(playbackAnalyserIntervalRef.current);
      setAudioLevel(0);
      setIsTestingLiveSpeaking(false);
      if (onEnd) onEnd();
    };

    utterance.onerror = () => {
      if (playbackAnalyserIntervalRef.current) clearInterval(playbackAnalyserIntervalRef.current);
      setAudioLevel(0);
      setIsTestingLiveSpeaking(false);
      if (onEnd) onEnd();
    };

    window.speechSynthesis.speak(utterance);
  };

  const playAudio = (url: string, fallbackText?: string, voice?: string) => {
    if (!url && !fallbackText) return;
    try {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
      }
      if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
      if (!url && fallbackText) {
        speakWithWebSpeech(fallbackText, voice);
        return;
      }
      const audio = new Audio(url);
      audioPlayerRef.current = audio;

      let fallbackDone = false;
      const doFallback = () => {
        if (fallbackDone) return;
        fallbackDone = true;
        if (fallbackText) {
          speakWithWebSpeech(fallbackText, voice);
        }
      };

      audio.onplay = () => {
        startLipSyncAnimationLoop(audio);
      };

      audio.onended = () => {
        if (playbackAnalyserIntervalRef.current) clearInterval(playbackAnalyserIntervalRef.current);
        setAudioLevel(0);
        setIsTestingLiveSpeaking(false);
      };

      audio.onerror = doFallback;
      audio.play().catch(err => {
        console.warn("Audio autoplay blocked, fallbacking:", err);
        doFallback();
      });
    } catch (err) {
      if (fallbackText) speakWithWebSpeech(fallbackText, voice);
    }
  };

  // Test Live Natural Speaking Demo Action
  const triggerLiveNaturalSpeakingDemo = () => {
    setIsTestingLiveSpeaking(true);
    const demoPhrases = [
      "哈啰！我是您的全数字人形象，我已经为您开启 3D Viseme 嘴型对口与自然说话模式，很高兴为您服务！",
      "嗯... 你好呀！我已经调整了自然语气与微表情，现在的口音和说话节奏是不是特别像真实的真人呀？",
      "对的！在 Telegram 同传通话里，我会配合您的每一句话进行生动的面部反应与实时翻译哦！"
    ];
    const phrase = demoPhrases[Math.floor(Math.random() * demoPhrases.length)];
    speakWithWebSpeech(phrase, currentAvatar.boundVoice);
  };

  const copyBilingualLog = () => {
    const text = messages.map(m => `[${m.senderName} - ${new Date(m.timestamp).toLocaleTimeString()}]\n原文: ${m.originalText}\n译文: ${m.translatedText}\n`).join("\n");
    navigator.clipboard.writeText(text);
    setCopiedId("all");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatTime = (secs: number) => {
    const mins = Math.floor(secs / 60);
    const rem = secs % 60;
    return `${mins.toString().padStart(2, "0")}:${rem.toString().padStart(2, "0")}`;
  };

  if (!isOpen) return null;

  const latestMsg = messages[messages.length - 1];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 backdrop-blur-md p-2 sm:p-5 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-5xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[94vh] max-h-[880px]"
        >
          {/* Top Bar Header */}
          <div className="flex items-center justify-between px-5 py-3 bg-slate-950 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="relative">
                {friendInfo.avatar ? (
                  <img src={friendInfo.avatar} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-700" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-sky-600 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                    {friendInfo.flag}
                  </div>
                )}
                {callStatus === "connected" && (
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-slate-950 rounded-full animate-pulse" />
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-slate-100">{friendInfo.name}</h3>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1">
                    <Radio className="w-2.5 h-2.5 animate-pulse text-emerald-400" />
                    {callMode === "digital_avatar" ? "数字人 3D 视频同传通话中" : "实时双语同传语音中"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                  <span className="text-emerald-400 font-mono font-medium">{formatTime(callDuration)}</span>
                  <span>•</span>
                  <span className="text-slate-300 font-mono">{friendInfo.phone}</span>
                  <span>•</span>
                  <span>{friendInfo.lang} ↔ 中文同传</span>
                </div>
              </div>
            </div>

            {/* Mode Switcher Tabs */}
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-slate-900 border border-slate-800 rounded-xl p-0.5">
                <button
                  type="button"
                  onClick={() => setCallMode("digital_avatar")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer ${
                    callMode === "digital_avatar"
                      ? "bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow"
                      : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  <Bot className="w-3.5 h-3.5" />
                  <span>数字人视频</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCallMode("voice")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer ${
                    callMode === "voice"
                      ? "bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow"
                      : "text-slate-400 hover:text-slate-200"
                  }`}
                >
                  <Phone className="w-3.5 h-3.5" />
                  <span>纯语音同传</span>
                </button>
              </div>

              <button
                type="button"
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2 rounded-xl border transition cursor-pointer ${showSettings ? "bg-sky-500/20 text-sky-300 border-sky-500/40" : "bg-slate-800/80 hover:bg-slate-800 text-slate-300 border-slate-700"}`}
                title="同传与音色角色设置"
              >
                <Settings className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={copyBilingualLog}
                className="p-2 rounded-xl bg-slate-800/80 hover:bg-slate-800 text-slate-300 border border-slate-700 transition cursor-pointer flex items-center gap-1 text-xs"
                title="复制双语通话记录"
              >
                {copiedId === "all" ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-xl bg-slate-800/80 hover:bg-red-950/60 hover:text-red-400 text-slate-400 border border-slate-700 transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Real Telegram Dispatch Banner */}
          {callInviteSentStatus && (
            <div className="bg-gradient-to-r from-sky-950/80 via-indigo-950/80 to-slate-950/80 border-b border-sky-500/20 px-5 py-2 flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 text-sky-300 font-medium truncate">
                <Video className="w-3.5 h-3.5 text-emerald-400 animate-pulse shrink-0" />
                <span className="truncate">{callInviteSentStatus}</span>
              </div>
              <button
                type="button"
                onClick={initiateTelegramCallToFriend}
                className="px-2.5 py-1 rounded bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/30 text-[10px] shrink-0 font-medium transition cursor-pointer"
              >
                重新呼叫 Telegram
              </button>
            </div>
          )}

          {/* Collapsible Voice Settings Panel */}
          {showSettings && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-slate-950 border-b border-slate-800 p-4 space-y-3.5 text-xs z-10 max-h-80 overflow-y-auto"
            >
              {/* Engine switcher tabs */}
              <div className="flex flex-wrap items-center justify-between gap-2 pb-2.5 border-b border-slate-800">
                <div className="flex items-center gap-1.5 bg-slate-900 p-1 rounded-xl border border-slate-800">
                  <button
                    type="button"
                    onClick={() => setCallVoiceEngine("voxcpm")}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1.5 ${
                      callVoiceEngine === "voxcpm"
                        ? "bg-sky-500 text-white shadow-sm shadow-sky-500/20"
                        : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    <span>⚡ OpenBMB/VoxCPM (推荐)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCallVoiceEngine("voicecraft")}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1.5 ${
                      callVoiceEngine === "voicecraft"
                        ? "bg-sky-500 text-white shadow-sm shadow-sky-500/20"
                        : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    <span>🎙️ VoiceCraft (Edge)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCallVoiceEngine("google")}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition cursor-pointer flex items-center gap-1.5 ${
                      callVoiceEngine === "google"
                        ? "bg-sky-500 text-white shadow-sm shadow-sky-500/20"
                        : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    <span>🌐 Google 官方翻译</span>
                  </button>
                </div>
                <span className="text-[11px] text-sky-400 font-mono">
                  {callVoiceEngine === "voxcpm"
                    ? "清华开源 2B 扩散自回归 Tokenizer-free 拟真大模型"
                    : callVoiceEngine === "voicecraft"
                    ? "22+ 种多国语种真实表现力 Edge 神经语音"
                    : "高速免 Key 谷歌翻译官方语音"}
                </span>
              </div>

              {callVoiceEngine === "voxcpm" ? (
                <div className="space-y-3">
                  {/* VoxCPM Prompt & Tone Bar */}
                  <div className="bg-slate-900/80 border border-sky-900/50 rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="font-bold text-sky-300 text-xs flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>VoxCPM 自然语言音色与情感提示词 (Prompt Control)</span>
                      </label>
                      <div className="flex flex-wrap items-center gap-1">
                        {[
                          { label: "知性商务", text: "温和知性的年轻商务女声，发音标准柔美，语调清晰亲切" },
                          { label: "阳光磁性", text: "阳光富有磁性的青年男声，语调自信稳重大气" },
                          { label: "元气少女", text: "活泼清甜的元气少女音，充满亲和力与感染力" },
                          { label: "软萌台腔", text: "温柔软萌的台湾女生腔调，亲切有礼，语速舒缓治愈" },
                          { label: "地道粤语", text: "地道香港粤语女声，发音纯正自然，音色有质感" }
                        ].map((tag) => (
                          <button
                            key={tag.label}
                            type="button"
                            onClick={() => setVoxcpmPrompt(tag.text)}
                            className="text-[10px] px-2 py-0.5 bg-sky-950/80 hover:bg-sky-900 text-sky-300 border border-sky-800/60 rounded-md transition"
                          >
                            {tag.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <input
                      type="text"
                      value={voxcpmPrompt}
                      onChange={(e) => setVoxcpmPrompt(e.target.value)}
                      placeholder="输入自然语言描述音色、语调与情感状态..."
                      className="w-full bg-slate-950 border border-slate-800 text-slate-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-sky-500"
                    />
                    <div className="flex flex-wrap items-center gap-3 pt-1 text-[11px] text-slate-400">
                      <div className="flex items-center gap-1.5">
                        <span>模型:</span>
                        <select
                          value={voxcpmModel}
                          onChange={(e) => setVoxcpmModel(e.target.value)}
                          className="bg-slate-950 border border-slate-800 text-slate-200 rounded px-2 py-0.5 text-xs font-mono"
                        >
                          <option value="VoxCPM2">VoxCPM2 (2B Tokenizer-free 推荐)</option>
                          <option value="VoxCPM-1.5B">VoxCPM-1.5B (轻量快速)</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span>扩散步数:</span>
                        <input
                          type="range"
                          min="10"
                          max="50"
                          value={voxcpmDiffusionSteps}
                          onChange={(e) => setVoxcpmDiffusionSteps(Number(e.target.value))}
                          className="w-20 accent-sky-500"
                        />
                        <span className="text-sky-400 font-mono">{voxcpmDiffusionSteps} 步</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-1 min-w-[200px]">
                        <span>私有端点:</span>
                        <input
                          type="text"
                          value={voxcpmServerUrl}
                          onChange={(e) => setVoxcpmServerUrl(e.target.value)}
                          placeholder="选填 http://127.0.0.1:8000/v1/audio/speech"
                          className="flex-1 bg-slate-950 border border-slate-800 text-slate-200 rounded px-2 py-0.5 text-[10px] font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  {/* VoxCPM Presets Grid */}
                  <div>
                    <div className="flex items-center justify-between pb-1.5">
                      <span className="font-bold text-slate-300 text-xs">
                        选择 VoxCPM 预设人声角色
                      </span>
                      <span className="text-[10px] text-slate-500">点击 🔊 试听 体验 2B 拟真人声</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {VOXCPM_CALL_VOICE_PRESETS.map((preset) => {
                        const isSelected = voxcpmPresetId === preset.id;
                        const isPlaying = playingVoice === preset.id;

                        return (
                          <div
                            key={preset.id}
                            className={`p-2.5 rounded-xl border transition-all flex items-center justify-between gap-2 ${
                              isSelected
                                ? "bg-sky-950/50 border-sky-500/70 shadow-sm shadow-sky-950/50"
                                : "bg-slate-900 border-slate-800 hover:border-slate-700"
                            }`}
                          >
                            <div className="truncate min-w-0">
                              <div className="font-bold text-slate-200 text-xs truncate flex items-center gap-1">
                                <span>{preset.flag}</span>
                                <span>{preset.name}</span>
                              </div>
                              <p className="text-[10px] text-slate-400 truncate mt-0.5">{preset.prompt}</p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => auditionVoxcpmVoice(preset)}
                                className={`px-2 py-1 rounded text-[10px] font-semibold flex items-center gap-0.5 transition cursor-pointer ${
                                  isPlaying
                                    ? "bg-emerald-500 text-white animate-pulse"
                                    : "bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 border border-sky-500/20"
                                }`}
                              >
                                <Volume1 className="w-3 h-3" />
                                <span>{isPlaying ? "播放" : "试听"}</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setVoxcpmPresetId(preset.id);
                                  setVoxcpmPrompt(preset.prompt);
                                }}
                                className={`px-2 py-1 rounded text-[10px] cursor-pointer ${
                                  isSelected
                                    ? "bg-sky-500 text-white font-bold"
                                    : "bg-slate-800 hover:bg-slate-700 text-slate-300"
                                }`}
                              >
                                选用
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between pb-2 border-b border-slate-800/80">
                    <span className="font-bold text-sky-400 text-xs flex items-center gap-1.5">
                      <Volume2 className="w-4 h-4" />
                      神经网络音色试听与选配 (22+ 种多语种角色)
                    </span>
                    <span className="text-[10px] text-slate-500">点击 🔊 试听 即可体验</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 pt-2">
                    {EXTENDED_TTS_ROLES.map((role) => {
                      const isSelectedForFriend = friendVoiceRole === role.voice;
                      const isPlaying = playingVoice === role.id;

                      return (
                        <div
                          key={role.id}
                          className={`p-2 rounded-xl border transition-all flex items-center justify-between gap-2 ${
                            isSelectedForFriend
                              ? "bg-sky-950/40 border-sky-500/60"
                              : "bg-slate-900 border-slate-800 hover:border-slate-700"
                          }`}
                        >
                          <div className="truncate min-w-0">
                            <div className="font-bold text-slate-200 text-xs truncate flex items-center gap-1">
                              <span>{role.flag}</span>
                              <span>{role.name}</span>
                            </div>
                            <p className="text-[10px] text-slate-400 truncate">{role.desc}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => auditionVoice(role)}
                              className={`px-2 py-1 rounded text-[10px] font-semibold flex items-center gap-0.5 transition ${
                                isPlaying
                                  ? "bg-emerald-500 text-white animate-pulse"
                                  : "bg-sky-500/10 text-sky-400 hover:bg-sky-500/20 border border-sky-500/20"
                              }`}
                            >
                              <Volume1 className="w-3 h-3" />
                              <span>{isPlaying ? "播放" : "试听"}</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setFriendVoiceRole(role.voice)}
                              className={`px-2 py-1 rounded text-[10px] ${
                                friendVoiceRole === role.voice
                                  ? "bg-sky-500 text-white font-bold"
                                  : "bg-slate-800 text-slate-400"
                              }`}
                            >
                              选用
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Main Display: Digital Avatar Video Screen or Waveform */}
          {callMode === "digital_avatar" ? (
            <div className="relative flex-1 bg-slate-950 border-b border-slate-800 flex flex-col md:flex-row overflow-hidden">
              
              {/* Left/Main Digital Avatar Viewport */}
              <div className="relative flex-1 bg-slate-950 flex flex-col items-center justify-center p-4 overflow-hidden min-h-[320px]">
                {/* Background Scene Container */}
                <div
                  className={`absolute inset-0 transition-all duration-700 ${
                    avatarScene === "office"
                      ? "bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950"
                      : avatarScene === "studio"
                      ? "bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950"
                      : avatarScene === "cyber"
                      ? "bg-gradient-to-br from-slate-950 via-rose-950 to-purple-950"
                      : avatarScene === "lounge"
                      ? "bg-gradient-to-br from-slate-950 via-emerald-950 to-teal-950"
                      : "bg-emerald-900/80" // Green Screen
                  }`}
                >
                  {/* Scene Decorative Grid / Light Nodes */}
                  <div className="absolute inset-0 bg-[radial-gradient(#38bdf8_1px,transparent_1px)] [background-size:24px_24px] opacity-15" />
                  <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />
                </div>

                {/* Video Badges Overlay */}
                <div className="absolute top-3 left-3 z-20 flex flex-wrap items-center gap-2">
                  <a
                    href="https://github.com/xszyou/Fay"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/20 backdrop-blur-md text-amber-300 border border-amber-500/40 flex items-center gap-1.5 shadow hover:bg-amber-500/30 transition"
                    title="点击查看 xszyou/Fay 开源数字人项目"
                  >
                    <Sparkles className="w-3 h-3 text-amber-400" />
                    <span>⚡ Fay 开源数字人 (xszyou/Fay)</span>
                  </a>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-black/60 backdrop-blur-md text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5 shadow">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                    WS 10002 直连 · 4K UE5 Viseme
                  </span>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-black/60 backdrop-blur-md text-sky-300 border border-sky-500/30 flex items-center gap-1 shadow">
                    <Bot className="w-3 h-3 text-sky-400" />
                    {currentAvatar.name.split(" ")[0]}
                  </span>
                </div>

                {/* Top Right Scene & Avatar Controls */}
                <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowRealismConsole(!showRealismConsole)}
                    className={`px-3 py-1.5 backdrop-blur-md rounded-xl text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer shadow border ${
                      showRealismConsole
                        ? "bg-amber-500/30 text-amber-300 border-amber-500/60 ring-2 ring-amber-500/30"
                        : "bg-black/60 hover:bg-black/80 text-amber-400 border-amber-500/30"
                    }`}
                  >
                    <Sliders className="w-3.5 h-3.5 text-amber-400" />
                    <span>仿真自然调优</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                    className="px-3 py-1.5 bg-black/60 hover:bg-black/80 backdrop-blur-md text-sky-300 border border-sky-500/30 rounded-xl text-xs font-semibold flex items-center gap-1 transition cursor-pointer shadow"
                  >
                    <Wand2 className="w-3.5 h-3.5 text-sky-400" />
                    <span>更换形象</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserCameraActive(!userCameraActive)}
                    className={`p-1.5 rounded-xl backdrop-blur-md border text-xs font-semibold transition cursor-pointer ${
                      userCameraActive
                        ? "bg-sky-500/30 text-sky-300 border-sky-500/50"
                        : "bg-black/60 text-slate-400 border-slate-700"
                    }`}
                    title="开启/关闭画中画我的镜头"
                  >
                    <Camera className="w-4 h-4" />
                  </button>
                </div>

                {/* Realism Fine-Tuning Drawer / Panel Overlay */}
                {showRealismConsole && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="absolute top-14 right-3 z-30 w-80 bg-slate-950/95 backdrop-blur-xl border border-amber-500/40 rounded-2xl p-4 shadow-2xl space-y-3.5 text-slate-200"
                  >
                    <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
                        <span className="font-bold text-sm text-white">仿真自然说话与微表情控制器</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowRealismConsole(false)}
                        className="text-slate-400 hover:text-white p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="space-y-3 text-xs">
                      {/* Viseme Sensitivity */}
                      <div>
                        <div className="flex justify-between text-slate-300 font-medium mb-1">
                          <span>👄 口型 LipSync 匹配度 (Viseme)</span>
                          <span className="text-amber-400 font-mono">{Math.round(visemeSensitivity * 100)}%</span>
                        </div>
                        <input
                          type="range"
                          min="0.8"
                          max="1.6"
                          step="0.1"
                          value={visemeSensitivity}
                          onChange={(e) => setVisemeSensitivity(parseFloat(e.target.value))}
                          className="w-full accent-amber-500 bg-slate-800 rounded-lg cursor-pointer h-1.5"
                        />
                      </div>

                      {/* Filler Words */}
                      <div className="flex items-center justify-between bg-slate-900/80 p-2.5 rounded-xl border border-slate-800">
                        <div>
                          <div className="font-semibold text-white">🗣️ 自然口癖与情绪停顿</div>
                          <div className="text-[10px] text-slate-400">自动注入“嗯...、那个、哈啰”等语气词</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFillerWordsEnabled(!fillerWordsEnabled)}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition ${
                            fillerWordsEnabled ? "bg-amber-500 justify-end" : "bg-slate-700 justify-start"
                          }`}
                        >
                          <span className="w-4 h-4 rounded-full bg-white shadow-md" />
                        </button>
                      </div>

                      {/* Test Live Speaking Button */}
                      <button
                        type="button"
                        onClick={triggerLiveNaturalSpeakingDemo}
                        disabled={isTestingLiveSpeaking}
                        className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-slate-950 font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 text-xs transition cursor-pointer"
                      >
                        {isTestingLiveSpeaking ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>正在演示真人自然口型与说话...</span>
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4 fill-slate-950" />
                            <span>▶️ 极速试听真人自然说话与 3D 口型</span>
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Digital Avatar 3D Animated Canvas / SVG Component */}
                <div className="relative z-10 flex flex-col items-center justify-center my-auto scale-100 transition-all duration-300">
                  {/* Ambient Glow Ring */}
                  <div className={`absolute w-72 h-72 rounded-full bg-gradient-to-tr ${currentAvatar.avatarColor} opacity-25 blur-2xl transition-all duration-300 ${
                    audioLevel > 10 || isProcessing ? "scale-125 opacity-40 animate-pulse" : "scale-100"
                  }`} />

                  {/* SVG or Photo Animated Photorealistic Digital Avatar Character with Micro-Physics */}
                  <div className="relative w-64 h-72 flex flex-col items-center justify-center">
                    
                    {currentAvatar.imageUrl ? (
                      <div
                        className="relative w-56 h-64 rounded-2xl overflow-hidden border-2 border-amber-500/50 shadow-2xl transition-transform duration-75 ease-out flex items-center justify-center bg-slate-950 group"
                        style={{
                          transform: `perspective(800px) rotateX(${headTilt * 1.1}deg) rotateY(${headTilt * 1.4}deg) translateY(${headY * head3DAmount}px) scale(${1 + Math.abs(chestBreathing) * 0.015})`,
                          transformOrigin: "center bottom"
                        }}
                      >
                        {/* Custom Uploaded Image */}
                        <img
                          src={currentAvatar.imageUrl}
                          alt={currentAvatar.name}
                          className="w-full h-full object-cover"
                        />

                        {/* Ambient Dark Gradient Bottom */}
                        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/20" />

                        {/* Realtime Photorealistic Eye Eyelid Blink Overlay */}
                        {blinkState && (
                          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                            <svg className="w-full h-full absolute inset-0" viewBox="0 0 100 100">
                              <ellipse cx="38" cy="38" rx="8" ry="4" fill="#020617" opacity="0.85" />
                              <path d="M 30 38 Q 38 42 46 38" stroke="#F1F5F9" strokeWidth="1.2" fill="none" />
                              <ellipse cx="62" cy="38" rx="8" ry="4" fill="#020617" opacity="0.85" />
                              <path d="M 54 38 Q 62 42 70 38" stroke="#F1F5F9" strokeWidth="1.2" fill="none" />
                            </svg>
                          </div>
                        )}

                        {/* 3D Facial Keypoints & Scan Mesh Overlay */}
                        <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-40" viewBox="0 0 100 100">
                          <ellipse cx="50" cy="45" rx="26" ry="32" fill="none" stroke="#F59E0B" strokeWidth="0.5" strokeDasharray="1.5 1.5" />
                          <circle cx="38" cy="38" r="1.5" fill="#F59E0B" className="animate-ping" />
                          <circle cx="62" cy="38" r="1.5" fill="#F59E0B" className="animate-ping" />
                          <circle cx="50" cy="48" r="1" fill="#F59E0B" />
                          <path d="M 40 58 Q 50 63 60 58" fill="none" stroke="#F59E0B" strokeWidth="0.8" />
                        </svg>

                        {/* Realtime Photorealistic Phoneme LipSync Mouth Viseme Overlay */}
                        {(() => {
                          const speakFactor = Math.min(28, Math.max(2, (audioLevel / 100) * 26 * visemeSensitivity + (isProcessing ? 12 : 0)));
                          const mouthWidth = 16 + speakFactor * 0.55;
                          const mouthHeight = 2.5 + speakFactor * 0.8;
                          const isOpen = speakFactor > 4;

                          return (
                            <div className="absolute bottom-[28%] left-1/2 -translate-x-1/2 pointer-events-none flex flex-col items-center justify-center">
                              <svg className="w-24 h-12 overflow-visible" viewBox="0 0 100 50">
                                {/* Outer Lip Shadow & Contour */}
                                <ellipse
                                  cx="50"
                                  cy="25"
                                  rx={mouthWidth}
                                  ry={mouthHeight}
                                  fill="#500724"
                                  opacity={isOpen ? 0.95 : 0.2}
                                />

                                {/* Inner Mouth Cavity */}
                                {isOpen && (
                                  <>
                                    <ellipse
                                      cx="50"
                                      cy="25"
                                      rx={mouthWidth * 0.85}
                                      ry={mouthHeight * 0.75}
                                      fill="#881337"
                                    />

                                    {/* Upper Teeth Row */}
                                    {mouthHeight > 5 && (
                                      <path
                                        d={`M ${50 - mouthWidth * 0.65} 22 Q 50 20 ${50 + mouthWidth * 0.65} 22 L ${50 + mouthWidth * 0.5} 25 L ${50 - mouthWidth * 0.5} 25 Z`}
                                        fill="#FFFFFF"
                                        opacity="0.9"
                                      />
                                    )}

                                    {/* Tongue Base */}
                                    {mouthHeight > 8 && (
                                      <ellipse
                                        cx="50"
                                        cy={25 + mouthHeight * 0.35}
                                        rx={mouthWidth * 0.5}
                                        ry={mouthHeight * 0.3}
                                        fill="#FB7185"
                                      />
                                    )}
                                  </>
                                )}

                                {/* Upper & Lower Lip Lines */}
                                <path
                                  d={`M ${50 - mouthWidth} 24 Q 50 ${24 - mouthHeight * 0.4} ${50 + mouthWidth} 24`}
                                  fill="none"
                                  stroke="#E11D48"
                                  strokeWidth="2.2"
                                  strokeLinecap="round"
                                />
                                {isOpen && (
                                  <path
                                    d={`M ${50 - mouthWidth * 0.8} 25 Q 50 ${25 + mouthHeight * 0.7} ${50 + mouthWidth * 0.8} 25`}
                                    fill="none"
                                    stroke="#FB7185"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    opacity="0.8"
                                  />
                                )}
                              </svg>
                            </div>
                          );
                        })()}

                        {/* Image Mesh Badge */}
                        <div className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-full bg-black/80 backdrop-blur-md border border-amber-500/40 text-[9px] font-mono text-amber-400 flex items-center gap-1">
                          <Camera className="w-2.5 h-2.5 text-amber-400" />
                          <span>图片 3D LipSync 逼真驱动</span>
                        </div>
                      </div>
                    ) : (
                    <svg
                      className="w-56 h-60 drop-shadow-2xl overflow-visible transition-transform duration-75 ease-out"
                      style={{
                        transform: `rotate(${headTilt}deg) translateY(${headY}px)`,
                        transformOrigin: "100px 170px"
                      }}
                      viewBox="0 0 200 220"
                    >
                      <defs>
                        {/* Radial Skin Subsurface Glow & Iris Gradients */}
                        <radialGradient id="skinGlow" cx="50%" cy="40%" r="60%">
                          <stop offset="0%" stopColor="#FFF1F2" />
                          <stop offset="70%" stopColor="#FECDD3" />
                          <stop offset="100%" stopColor="#FDA4AF" />
                        </radialGradient>

                        <radialGradient id="irisGrad" cx="40%" cy="40%" r="60%">
                          <stop offset="0%" stopColor="#38BDF8" />
                          <stop offset="60%" stopColor="#0284C7" />
                          <stop offset="100%" stopColor="#0F172A" />
                        </radialGradient>

                        <linearGradient id="hairHighlight" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.4" />
                          <stop offset="100%" stopColor={currentAvatar.hairColor} stopOpacity="1" />
                        </linearGradient>

                        <linearGradient id="suitGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={currentAvatar.outfitColor} />
                          <stop offset="100%" stopColor="#090D16" />
                        </linearGradient>
                      </defs>

                      {/* Hair Back */}
                      <path
                        d="M 28 110 C 18 35, 182 35, 172 110 C 182 165, 172 185, 162 195 C 150 175, 165 110, 140 78 C 110 48, 90 48, 60 78 C 35 110, 50 175, 38 195 Z"
                        fill={currentAvatar.hairColor}
                        opacity="0.95"
                      />

                      {/* Head Base & Skin Shading */}
                      <ellipse cx="100" cy="100" rx="55" ry="65" fill="#F43F5E" opacity="0.12" />
                      <ellipse cx="100" cy="98" rx="52" ry="62" fill="url(#skinGlow)" />

                      {/* Ears & Earrings */}
                      <circle cx="45" cy="100" r="10" fill="#FECDD3" />
                      <circle cx="155" cy="100" r="10" fill="#FECDD3" />
                      <circle cx="45" cy="110" r="3" fill="#BAE6FD" />
                      <circle cx="155" cy="110" r="3" fill="#BAE6FD" />

                      {/* Eyebrows & Eyes with Human Blinking & Pupil Micro-Saccades */}
                      {blinkState ? (
                        <>
                          {/* Closed Eye Eyelash Curves */}
                          <path d="M 66 90 Q 80 96 94 90" fill="none" stroke="#0F172A" strokeWidth="3" strokeLinecap="round" />
                          <path d="M 106 90 Q 120 96 134 90" fill="none" stroke="#0F172A" strokeWidth="3" strokeLinecap="round" />
                        </>
                      ) : (
                        <>
                          {/* Organic Eyebrow Flexing when Speaking */}
                          {(() => {
                            const eyebrowLift = (audioLevel > 15 ? -2 : 0) + (isProcessing ? -1.5 : 0);
                            return (
                              <g>
                                <path
                                  d={`M 66 ${79 + eyebrowLift} Q 80 ${72 + eyebrowLift} 94 ${78 + eyebrowLift}`}
                                  fill="none"
                                  stroke={currentAvatar.hairColor}
                                  strokeWidth="3.8"
                                  strokeLinecap="round"
                                />
                                <path
                                  d={`M 106 ${78 + eyebrowLift} Q 120 ${72 + eyebrowLift} 134 ${79 + eyebrowLift}`}
                                  fill="none"
                                  stroke={currentAvatar.hairColor}
                                  strokeWidth="3.8"
                                  strokeLinecap="round"
                                />
                              </g>
                            );
                          })()}

                          {/* Eye Sockets */}
                          <circle cx="80" cy="90" r="11" fill="#FFFFFF" stroke="#CBD5E1" strokeWidth="0.8" />
                          <circle cx="120" cy="90" r="11" fill="#FFFFFF" stroke="#CBD5E1" strokeWidth="0.8" />

                          {/* Irises with Pupil Shift */}
                          <circle cx={80 + pupilShift.x} cy={90 + pupilShift.y} r="6.5" fill="url(#irisGrad)" />
                          <circle cx={120 + pupilShift.x} cy={90 + pupilShift.y} r="6.5" fill="url(#irisGrad)" />

                          {/* Pupil Center */}
                          <circle cx={80 + pupilShift.x} cy={90 + pupilShift.y} r="3" fill="#020617" />
                          <circle cx={120 + pupilShift.x} cy={90 + pupilShift.y} r="3" fill="#020617" />

                          {/* Specular Glint Refraction */}
                          <circle cx={82 + pupilShift.x} cy={88 + pupilShift.y} r="2" fill="#FFFFFF" opacity="0.9" />
                          <circle cx={122 + pupilShift.x} cy={88 + pupilShift.y} r="2" fill="#FFFFFF" opacity="0.9" />
                        </>
                      )}

                      {/* Nose Bridge & Tip */}
                      <path d="M 98 94 Q 100 106 102 108 M 96 108 Q 100 111 104 108" fill="none" stroke="#F43F5E" strokeWidth="2.2" strokeLinecap="round" opacity="0.85" />

                      {/* Cheek Blush */}
                      <ellipse cx="66" cy="107" rx="11" ry="6" fill="#F43F5E" opacity="0.22" />
                      <ellipse cx="134" cy="107" rx="11" ry="6" fill="#F43F5E" opacity="0.22" />

                      {/* Realtime Phoneme LipSync Mouth with Upper Teeth & Tongue */}
                      {(() => {
                        const speakFactor = Math.min(28, Math.max(3.5, (audioLevel / 100) * 25 + (isProcessing ? 14 : 0)));
                        const rx = 13 + speakFactor * 0.45;
                        const ry = 3.5 + speakFactor * 0.85;

                        return (
                          <g>
                            {/* Mouth Opening Base */}
                            <ellipse cx="100" cy="123" rx={rx} ry={ry} fill="#881337" />
                            
                            {/* Upper Teeth Row */}
                            {ry > 7 && (
                              <path
                                d={`M ${100 - rx * 0.75} 121 Q 100 120 ${100 + rx * 0.75} 121 L ${100 + rx * 0.6} 124 L ${100 - rx * 0.6} 124 Z`}
                                fill="#FFFFFF"
                                opacity="0.95"
                              />
                            )}

                            {/* Tongue Base */}
                            {ry > 10 && (
                              <ellipse cx="100" cy={123 + ry * 0.4} rx={rx * 0.55} ry={ry * 0.35} fill="#FB7185" />
                            )}

                            {/* Lip Contour Highlights */}
                            <path
                              d={`M ${100 - rx} 122 Q 100 ${122 - ry * 0.5} ${100 + rx} 122`}
                              fill="none"
                              stroke="#E11D48"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                            />
                          </g>
                        );
                      })()}

                      {/* Hair Front Bangs & Specular Highlight */}
                      <path
                        d="M 42 75 Q 80 38 100 62 Q 120 38 158 75 Q 130 48 100 50 Q 70 48 42 75 Z"
                        fill="url(#hairHighlight)"
                      />

                      {/* Neck with Shadow */}
                      <rect x="88" y="150" width="24" height="25" fill="#FECDD3" rx="4" />
                      <rect x="88" y="150" width="24" height="6" fill="#FDA4AF" opacity="0.4" />

                      {/* Shoulders & Suit with Chest Breathing Physics */}
                      <g style={{ transform: `translateY(${chestBreathing * 0.8}px)` }}>
                        <path
                          d="M 38 215 Q 48 168 88 170 L 100 188 L 112 170 Q 152 168 162 215 Z"
                          fill="url(#suitGrad)"
                        />
                        {/* Inner Shirt & Tie Collar */}
                        <polygon points="88,170 100,192 112,170" fill="#FFFFFF" />
                        <polygon points="98,175 100,205 102,175" fill="#0284C7" />
                      </g>
                    </svg>
                    )}

                    {/* Active Gesture Pop-up Indicator */}
                    {activeGesture && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.8 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="absolute -top-8 px-3 py-1 bg-gradient-to-r from-sky-500 to-indigo-600 text-white rounded-full text-xs font-bold shadow-lg flex items-center gap-1.5 border border-white/20 animate-bounce"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                        <span>
                          {activeGesture === "wave" && "👋 正在进行打招呼动作"}
                          {activeGesture === "smile" && "😊 展示亲切微笑肢体"}
                          {activeGesture === "handshake" && "🤝 伸出手臂进行商务握手"}
                          {activeGesture === "nod" && "💡 点头认可强调观点"}
                          {activeGesture === "applause" && "👏 热烈鼓掌赞赏"}
                        </span>
                      </motion.div>
                    )}
                  </div>

                  {/* Character Name & Status Tag */}
                  <div className="mt-2 text-center">
                    <div className="text-sm font-bold text-white flex items-center justify-center gap-1.5">
                      <span>{currentAvatar.flag}</span>
                      <span>{currentAvatar.name.split(" ")[0]}</span>
                      <span className="text-[10px] text-sky-400 font-normal">({currentAvatar.title})</span>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      绑定声音: <span className="text-sky-300 font-mono">{currentAvatar.boundVoiceName}</span>
                    </div>
                  </div>
                </div>

                {/* Floating Dual-language Captions Overlay at Bottom of Video */}
                <div className="w-full max-w-2xl mt-auto z-20 bg-black/75 backdrop-blur-md border border-slate-800 rounded-2xl p-3 shadow-2xl space-y-1">
                  <div className="flex items-center justify-between text-[10px] text-slate-400 font-medium">
                    <span className="text-sky-400 font-bold flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-sky-400 animate-spin" />
                      实时双语同传字幕 (Dual-Language Captions)
                    </span>
                    <span className="font-mono text-emerald-400">延迟 ~110ms</span>
                  </div>

                  {latestMsg ? (
                    <div className="text-xs space-y-1">
                      <p className="text-slate-300 font-sans">
                        <span className="text-slate-500 font-bold">[{latestMsg.senderName} 原文]:</span> {latestMsg.originalText}
                      </p>
                      <p className="text-sky-300 font-bold font-sans">
                        <span className="text-sky-400 font-bold">[{latestMsg.targetLang} 译文]:</span> {latestMsg.translatedText}
                      </p>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-400 py-1 italic text-center">
                      通话已建立，请说话开启实时 AI 数字人视频同传字幕...
                    </div>
                  )}
                </div>

                {/* User Camera PIP Selfie Overlay */}
                {userCameraActive && (
                  <motion.div
                    drag
                    dragConstraints={{ left: -100, right: 100, top: -100, bottom: 100 }}
                    className="absolute bottom-4 right-4 z-30 w-32 h-24 bg-slate-900 border-2 border-sky-500/60 rounded-xl overflow-hidden shadow-2xl flex flex-col items-center justify-center cursor-move"
                  >
                    <div className="absolute top-1 left-1 px-1.5 py-0.2 rounded bg-black/70 text-[9px] text-sky-400 font-bold">
                      我的镜头
                    </div>
                    <User className="w-8 h-8 text-slate-400" />
                    <span className="text-[9px] text-slate-400 mt-1 font-medium">前端 Web 摄像头</span>
                    {isListening && (
                      <span className="absolute bottom-1 right-1 w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                    )}
                  </motion.div>
                )}

                {/* Avatar Picker Drawer Modal */}
                {showAvatarPicker && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    className="absolute inset-x-4 bottom-16 z-40 bg-slate-900/95 backdrop-blur-xl border border-slate-700 rounded-2xl p-4 shadow-2xl space-y-3"
                  >
                    <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                      <span className="font-bold text-white text-xs flex items-center gap-1.5">
                        <Bot className="w-4 h-4 text-sky-400" />
                        选择对话的数字人形象 (3D 全息/写实主播/二次元)
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowAvatarPicker(false)}
                        className="text-slate-400 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-h-56 overflow-y-auto">
                      {avatarsList.map((avatar) => {
                        const isSelected = activeAvatarId === avatar.id;
                        return (
                          <div
                            key={avatar.id}
                            onClick={() => {
                              setActiveAvatarId(avatar.id);
                              setShowAvatarPicker(false);
                            }}
                            className={`p-2.5 rounded-xl border transition cursor-pointer flex flex-col justify-between ${
                              isSelected
                                ? "bg-sky-950/60 border-sky-500 text-white shadow-lg"
                                : "bg-slate-950/80 border-slate-800 hover:border-slate-700 text-slate-300"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${avatar.avatarColor} flex items-center justify-center font-bold text-xs shrink-0`}>
                                {avatar.flag}
                              </div>
                              <div className="truncate">
                                <div className="text-xs font-bold truncate">{avatar.name.split(" ")[0]}</div>
                                <div className="text-[10px] text-slate-400 truncate">{avatar.country}</div>
                              </div>
                            </div>
                            <div className="text-[10px] text-slate-400 mt-2 line-clamp-1">
                              {avatar.title}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Right Sidebar: Digital Avatar Interactive Controls & Scenes */}
              <div className="w-full md:w-64 bg-slate-900 border-t md:border-t-0 md:border-l border-slate-800 p-3.5 space-y-4 flex flex-col justify-between text-xs overflow-y-auto">
                {/* Gesture Action Triggers */}
                <div className="space-y-2">
                  <span className="font-bold text-slate-200 block text-xs flex items-center gap-1.5">
                    <Hand className="w-4 h-4 text-sky-400" />
                    数字人互动肢体手势
                  </span>
                  <p className="text-[10px] text-slate-400">点击按钮指挥数字人作出对应实时动态手势：</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleTriggerGesture("wave")}
                      className="p-2 bg-slate-950 hover:bg-sky-950/50 border border-slate-800 hover:border-sky-500/40 text-slate-300 hover:text-sky-300 rounded-xl transition text-[11px] flex items-center justify-center gap-1 cursor-pointer font-medium"
                    >
                      <span>👋 打招呼</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTriggerGesture("smile")}
                      className="p-2 bg-slate-950 hover:bg-sky-950/50 border border-slate-800 hover:border-sky-500/40 text-slate-300 hover:text-sky-300 rounded-xl transition text-[11px] flex items-center justify-center gap-1 cursor-pointer font-medium"
                    >
                      <span>😊 亲切微笑</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTriggerGesture("handshake")}
                      className="p-2 bg-slate-950 hover:bg-sky-950/50 border border-slate-800 hover:border-sky-500/40 text-slate-300 hover:text-sky-300 rounded-xl transition text-[11px] flex items-center justify-center gap-1 cursor-pointer font-medium"
                    >
                      <span>🤝 商务握手</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTriggerGesture("nod")}
                      className="p-2 bg-slate-950 hover:bg-sky-950/50 border border-slate-800 hover:border-sky-500/40 text-slate-300 hover:text-sky-300 rounded-xl transition text-[11px] flex items-center justify-center gap-1 cursor-pointer font-medium"
                    >
                      <span>💡 点头强调</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTriggerGesture("applause")}
                      className="col-span-2 p-2 bg-slate-950 hover:bg-sky-950/50 border border-slate-800 hover:border-sky-500/40 text-slate-300 hover:text-sky-300 rounded-xl transition text-[11px] flex items-center justify-center gap-1 cursor-pointer font-medium"
                    >
                      <span>👏 鼓掌赞赏</span>
                    </button>
                  </div>
                </div>

                {/* Virtual Background Scene Selector */}
                <div className="space-y-2 pt-2 border-t border-slate-800">
                  <span className="font-bold text-slate-200 block text-xs flex items-center gap-1.5">
                    <Palette className="w-4 h-4 text-emerald-400" />
                    虚拟背景演播环境
                  </span>
                  <div className="space-y-1">
                    {[
                      { id: "office", name: "🏢 商务高端会议室" },
                      { id: "studio", name: "🎥 4K 3D 虚拟演播厅" },
                      { id: "cyber", name: "🌃 赛博霓虹空间" },
                      { id: "lounge", name: "🌿 惬意雅致客厅" },
                      { id: "green", name: "🟢 绿幕抠像背景" },
                    ].map((scene) => (
                      <button
                        key={scene.id}
                        type="button"
                        onClick={() => setAvatarScene(scene.id as any)}
                        className={`w-full p-2 rounded-xl text-[11px] text-left transition flex items-center justify-between cursor-pointer ${
                          avatarScene === scene.id
                            ? "bg-sky-500/20 text-sky-300 border border-sky-500/40 font-bold"
                            : "bg-slate-950 text-slate-400 hover:text-slate-200 border border-slate-800"
                        }`}
                      >
                        <span>{scene.name}</span>
                        {avatarScene === scene.id && <Check className="w-3.5 h-3.5 text-sky-400" />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick Info & Stats */}
                <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 text-[10px] text-slate-400 space-y-1">
                  <div className="flex justify-between">
                    <span>人声同传引擎:</span>
                    <span className="text-emerald-400 font-mono">Gemini Flash</span>
                  </div>
                  <div className="flex justify-between">
                    <span>面部 LipSync:</span>
                    <span className="text-sky-400 font-mono font-bold">100% 实时同步</span>
                  </div>
                  <div className="flex justify-between">
                    <span>双语同传字幕:</span>
                    <span className="text-amber-400 font-mono">已激活</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Voice-Only Mode Display */
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-900/60 font-sans">
              <div className="relative bg-slate-950/80 border border-slate-800 rounded-2xl p-4 overflow-hidden flex flex-col items-center justify-center min-h-[130px]">
                <div className={`absolute inset-0 bg-gradient-to-r from-sky-600/10 via-indigo-600/10 to-purple-600/10 opacity-50 ${isListening ? "animate-pulse" : ""}`} />

                <div className="relative z-10 flex items-center gap-6">
                  <div className="flex flex-col items-center gap-1">
                    <div className={`relative w-12 h-12 rounded-full bg-slate-800 border-2 flex items-center justify-center transition ${isListening && audioLevel > 5 ? "border-emerald-400 shadow-lg shadow-emerald-500/20" : "border-slate-700"}`}>
                      <User className="w-6 h-6 text-slate-300" />
                      {isListening && audioLevel > 5 && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-ping" />
                      )}
                    </div>
                    <span className="text-[11px] font-medium text-slate-400">我 (中文)</span>
                  </div>

                  <div className="flex items-center gap-1.5 h-12 px-4 bg-slate-900/80 border border-slate-800/80 rounded-full">
                    {[40, 75, 30, 90, 60, 100, 45, 80, 50, 95, 35, 70].map((heightPct, idx) => {
                      const activeHeight = isListening ? Math.max(15, (audioLevel / 100) * heightPct) : 12;
                      return (
                        <div
                          key={idx}
                          className="w-1.5 rounded-full transition-all duration-75 bg-gradient-to-t from-sky-500 to-emerald-400"
                          style={{ height: `${activeHeight}%` }}
                        />
                      );
                    })}
                  </div>

                  <div className="flex flex-col items-center gap-1">
                    <div className="relative w-12 h-12 rounded-full bg-slate-800 border-2 border-sky-500/50 flex items-center justify-center text-xl shadow-lg shadow-sky-500/10 overflow-hidden">
                      {friendInfo.avatar ? (
                        <img src={friendInfo.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        friendInfo.flag
                      )}
                    </div>
                    <span className="text-[11px] font-medium text-slate-400">{friendInfo.name.split(" ")[0]}</span>
                  </div>
                </div>

                {currentSpeech && (
                  <div className="relative z-10 mt-3 text-xs text-emerald-300 font-medium bg-emerald-950/60 border border-emerald-800/60 rounded-full px-3 py-1 flex items-center gap-2 animate-pulse">
                    <Mic className="w-3.5 h-3.5 text-emerald-400 animate-spin" />
                    <span>捕获语音中: “{currentSpeech}”</span>
                  </div>
                )}

                {isProcessing && (
                  <div className="relative z-10 mt-3 text-xs text-sky-300 font-medium bg-sky-950/60 border border-sky-800/60 rounded-full px-3 py-1 flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-sky-400 animate-spin" />
                    <span>AI 极速双语转译与人声合成中...</span>
                  </div>
                )}
              </div>

              {/* Subtitle Messages Stream */}
              <div className="space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center py-10 text-slate-500 text-xs space-y-2">
                    <Globe className="w-8 h-8 mx-auto text-slate-600 opacity-60" />
                    <p className="text-slate-300 font-semibold">与好友【{friendInfo.name}】的实时双语通话已建立！</p>
                    <p className="text-slate-400">对着麦克风说话，系统将实时把您的母语翻译成好友语言并播报。</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isMe = msg.sender === "user";
                    return (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}
                      >
                        <div className="flex items-center gap-1.5 mb-1 text-[11px] text-slate-400">
                          <span className="font-semibold text-slate-300">{msg.senderName}</span>
                          <span>•</span>
                          <span className="font-mono text-[10px]">{new Date(msg.timestamp).toLocaleTimeString()}</span>
                          {msg.syncedToChat && (
                            <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.2 rounded border border-emerald-500/20">
                              已同步至Telegram
                            </span>
                          )}
                        </div>

                        <div className={`max-w-[85%] rounded-2xl p-3.5 space-y-2 border ${
                          isMe
                            ? "bg-sky-950/40 border-sky-800/60 text-slate-100 rounded-tr-none"
                            : "bg-slate-950/80 border-slate-800 text-slate-100 rounded-tl-none"
                        }`}>
                          <div>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">
                              🗣️ 原文 ({msg.srcLang}):
                            </span>
                            <p className="text-xs leading-relaxed font-sans">{msg.originalText}</p>
                          </div>

                          <div className="pt-2 border-t border-slate-800/80">
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <span className="text-[10px] font-bold text-sky-400 uppercase tracking-wider">
                                ✨ 极速翻译 ({msg.targetLang}):
                              </span>
                              <div className="flex items-center gap-1.5">
                                {msg.audioUrl && (
                                  <button
                                    type="button"
                                    onClick={() => playAudio(msg.audioUrl!)}
                                    className="text-sky-400 hover:text-sky-300 flex items-center gap-1 text-[10px] font-medium transition bg-sky-500/10 px-2 py-0.5 rounded-md border border-sky-500/20 cursor-pointer"
                                  >
                                    <Play className="w-3 h-3 fill-sky-400" />
                                    播放朗读
                                  </button>
                                )}
                                {!msg.syncedToChat && onSendCallLogToChat && (
                                  <button
                                    type="button"
                                    onClick={() => handleSyncSingleMsg(msg)}
                                    className="text-slate-400 hover:text-white text-[10px] bg-slate-800 px-2 py-0.5 rounded-md border border-slate-700 cursor-pointer"
                                    title="将此条双语译文发送给好友 Telegram"
                                  >
                                    💬 同步发送
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-xs font-medium text-sky-200 leading-relaxed font-sans">
                              {msg.translatedText}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          )}

          {/* Quick Speak Testing Buttons */}
          <div className="px-4 py-2 bg-slate-950/90 border-t border-slate-800/60 flex items-center gap-2 overflow-x-auto text-[11px] whitespace-nowrap scrollbar-none shrink-0">
            <span className="text-slate-500 font-semibold text-[10px]">💡 快捷测试常用句:</span>
            <button
              type="button"
              onClick={() => sendManualSpeech("很高兴认识你！今天工作顺利吗？")}
              className="px-2.5 py-1 bg-slate-900 hover:bg-sky-950/60 text-slate-300 hover:text-sky-300 border border-slate-800 rounded-full transition cursor-pointer"
            >
              👋 问候打招呼
            </button>
            <button
              type="button"
              onClick={() => sendManualSpeech("我们产品的质量非常好，支持全球发货与售后保证！")}
              className="px-2.5 py-1 bg-slate-900 hover:bg-sky-950/60 text-slate-300 hover:text-sky-300 border border-slate-800 rounded-full transition cursor-pointer"
            >
              🛍️ 商务洽谈发货
            </button>
            <button
              type="button"
              onClick={() => sendManualSpeech("今晚要不要一起去尝尝特色美食？")}
              className="px-2.5 py-1 bg-slate-900 hover:bg-sky-950/60 text-slate-300 hover:text-sky-300 border border-slate-800 rounded-full transition cursor-pointer"
            >
              🍲 约饭日常交谈
            </button>
          </div>

          {/* Bottom Call Controls & Text Fallback Input */}
          <div className="p-3.5 bg-slate-950 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
            <div className="w-full sm:w-auto flex-1 flex items-center gap-2">
              <input
                type="text"
                placeholder="在此输入想要实时翻译并生成数字人语音/动作给好友的话..."
                value={currentSpeech}
                onChange={(e) => setCurrentSpeech(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && currentSpeech) {
                    sendManualSpeech(currentSpeech);
                  }
                }}
                className="w-full bg-slate-900 border border-slate-800 text-slate-200 text-xs rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-sky-500"
              />
              <button
                type="button"
                onClick={() => sendManualSpeech(currentSpeech)}
                disabled={!currentSpeech.trim() || isProcessing}
                className="px-4 py-2.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-medium rounded-xl transition flex items-center gap-1 shrink-0 cursor-pointer shadow-md"
              >
                <Send className="w-3.5 h-3.5" />
                <span>发送同传</span>
              </button>
            </div>

            <div className="flex items-center gap-2.5 shrink-0">
              <button
                type="button"
                onClick={() => setIsMicMuted(!isMicMuted)}
                className={`p-2.5 rounded-xl border transition flex items-center gap-1.5 text-xs font-semibold cursor-pointer ${
                  isMicMuted
                    ? "bg-red-500/20 text-red-400 border-red-500/40"
                    : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20"
                }`}
                title={isMicMuted ? "取消静音麦克风" : "静音麦克风"}
              >
                {isMicMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4 text-emerald-400 animate-pulse" />}
                <span>{isMicMuted ? "麦克风已静音" : "麦克风工作中"}</span>
              </button>

              <button
                type="button"
                onClick={() => setIsSpeakerMuted(!isSpeakerMuted)}
                className={`p-2.5 rounded-xl border transition cursor-pointer ${
                  isSpeakerMuted
                    ? "bg-red-500/20 text-red-400 border-red-500/40"
                    : "bg-slate-800 text-slate-200 hover:bg-slate-700 border-slate-700"
                }`}
                title={isSpeakerMuted ? "开启扬声器" : "静音扬声器"}
              >
                {isSpeakerMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-sky-400" />}
              </button>

              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white font-bold text-xs shadow-lg shadow-red-600/30 flex items-center gap-1.5 transition cursor-pointer"
              >
                <PhoneOff className="w-4 h-4 fill-white" />
                <span>挂断通话</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
